// Copyright 2016 Sorama B.V.

import { autoinject, BindingEngine, Disposable, transient } from "aurelia-framework";
import { WorkflowModalCatalog } from "catalogs/WorkflowModalCatalog";
import { Spectrum, StreamSpectrum } from "datatypes/SoramaLocalApi";
import {
    IWorkflowModuleInstance,
    IWorkflowModuleLifetime,
    IWorkflowModuleModalInstance,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import { userSetting } from "libraries/WorkflowFramework/UserSettingsAttribute";
import { SettingsModalState } from "modules/modals/SettingsModal";
import { SPLEntry } from "modules/workflows/Streamer/SPL/SPLEntry";
import { FrequencySelectionService, SelectableRange } from "services/FrequencySelection/FrequencySelectionService";
import { IMeasurementPreviewService } from "services/Measurement/IMeasurementPreviewService";
import { NotificationUrgency } from "services/Notification/INotificationService";
import { Frequency } from "units/Frequency";
import { Pressure } from "units/Pressure";
import { Unit, UnitValue } from "units/Unit";
import { UnitSpectrum } from "units/UnitSpectrum";
import { WeightedSpectrum } from "units/WeightedSpectrum";
import { AWeighting, BWeighting, CWeighting, Weighting, ZWeighting } from "units/Weighting";

type selectableWeighting = { weighting: Weighting; unit: Unit };

/**
 * MODULE OPERATION DESCRIPTION - DBAModule
 * The far field module is a display module that continuously displays dba values
 * The stream is active when the 'isSpectrumAvailable' properties on the IMeasurementPreviewService is true.
 * Frequency selection for the beamforming projections are provided by the workflowproperty named 'selectedFrequencies'
 */
@transient()
@autoinject
export class SPLMonitorModule implements IWorkflowModuleLifetime {
    //#region Other Properties
    private streamPromise: Promise<unknown>;
    private moduleInstance: IWorkflowModuleInstance;
    private streamerSPLSettingsModal: IWorkflowModuleModalInstance<SettingsModalState<SPLMonitorModule>, void>;
    private subscriptions: Disposable[] = [];
    private viewActive: boolean;

    public splEntries: SPLEntry[];

    public static availableWeightings: selectableWeighting[] = [
        { weighting: new ZWeighting(), unit: Pressure.dbSPL },
        { weighting: new AWeighting(), unit: Pressure.dbASPL },
        { weighting: new BWeighting(), unit: Pressure.dbBSPL },
        { weighting: new CWeighting(), unit: Pressure.dbCSPL },
    ];

    public get availableWeightings(): selectableWeighting[] {
        return SPLMonitorModule.availableWeightings;
    }

    public get frequencyRanges(): SelectableRange[] {
        return this.frequencySelectionService.ranges;
    }

    public set frequencyRanges(value: SelectableRange[]) {
        // Replace all ranges without changing the reference
        this.frequencySelectionService.ranges.splice(0, this.frequencySelectionService.ranges.length, ...value);
    }

    @userSetting({
        toStringConverter: (x: selectableWeighting) => x.unit.unit,
        fromStringConverter: SPLMonitorModule.stringToSelectedUnit,
    })
    public selectedWeighting: selectableWeighting = SPLMonitorModule.availableWeightings[1];

    @userSetting() public windowSizes: number[] = [1, 0.5, 0.25, 0.125, 0.04];
    @userSetting() public selectedWindowSize = 0.125;

    //#endregion

    constructor(
        private readonly measurementPreviewService: IMeasurementPreviewService,
        private readonly bindingEngine: BindingEngine,
        private readonly frequencySelectionService: FrequencySelectionService
    ) {}

    //#region Lifecycle functions
    public activateModule(moduleInstance: IWorkflowModuleInstance): void {
        this.moduleInstance = moduleInstance;

        this.subscriptions = [
            this.bindingEngine
                .propertyObserver(this.measurementPreviewService, "isSpectrumAvailable")
                .subscribe(this.isSpectrumAvailableChanged),
        ];

        this.splEntries = [
            new SPLEntry(this, new UnitValue(0, this.selectedWeighting.unit), this.selectedWeighting.weighting),
        ];

        this.viewActive = true;
        this.isSpectrumAvailableChanged();
    }

    public deactivateModule(): void {
        this.viewActive = false;

        while (this.subscriptions.length > 0) this.subscriptions.pop().dispose();
    }

    //#endregion

    //#region Stream functions

    /**
     * This method updates all state related to the status of the video or sound measurements
     */
    private isSpectrumAvailableChanged = () => {
        // Check current status of measurements
        if (this.measurementPreviewService.isSpectrumAvailable.available && !this.streamPromise) {
            // Start streaming
            this.streamPromise = this.beginGetSpectrum();
        } else if (this.measurementPreviewService.isSpectrumAvailable.reason) {
            this.moduleInstance
                .createNotification()
                .withUrgency(
                    this.measurementPreviewService.isSpectrumAvailable.inProgress
                        ? NotificationUrgency.Info
                        : NotificationUrgency.Warning
                )
                .withMessage(this.measurementPreviewService.isSpectrumAvailable.reason)
                .show();
        }
    };

    /**
     * Starts the complete streaming chain
     */
    private startStream(): void {
        if (this.streamPromise) throw new Error("Stream is already active");

        // Start streaming
        this.streamPromise = this.beginGetSpectrum();
    }

    /**
     * Stops the streaming chain, when the returned promise resolves the stream is garantueed to be stopped
     */
    private stopStream(): Promise<void> {
        // Wait for the steam promise to finish and reset all state to default
        const promise = this.streamPromise ? this.streamPromise : Promise.resolve();

        return promise.catch().then(() => {
            this.streamPromise = null;
        });
    }

    private beginGetSpectrum(): Promise<void> {
        return this.measurementPreviewService
            .previewSpectrum({
                windowSize: Math.round(this.selectedWindowSize * 46875),
            })
            .then((x) => this.endGetSpectrum(x))
            .catch(() => {
                return this.exitGetSpectrum();
            });
    }

    private endGetSpectrum(spectrum: StreamSpectrum): Promise<void> | void {
        const unitSpectrum = UnitSpectrum.FromSpectrum(Frequency.hertz, Pressure.pascal, <Spectrum>{
            data: spectrum.dataPoints,
            properties: {
                timeRange: {
                    from: 0,
                    to: 0,
                },
            },
        });

        for (let i = 0; i < SPLMonitorModule.availableWeightings.length; i++) {
            if (!this.splEntries.some((x) => x.weighting === SPLMonitorModule.availableWeightings[i].weighting))
                continue;

            const weightedSpectrum = WeightedSpectrum.FromUnitSpectrum(new ZWeighting(), unitSpectrum);
            weightedSpectrum.weighting = SPLMonitorModule.availableWeightings[i].weighting;

            this.splEntries.forEach((x) => {
                if (x.weighting === SPLMonitorModule.availableWeightings[i].weighting) {
                    x.update(weightedSpectrum);
                }
            });
        }

        // Request new hologram if needed
        return this.exitGetSpectrum();
    }

    private exitGetSpectrum(): Promise<void> | void {
        if (this.measurementPreviewService.isSpectrumAvailable.available && this.viewActive)
            return this.beginGetSpectrum();
        else this.streamPromise = null;
    }

    //#endregion

    public addEntry(selectedWeighting: { weighting: Weighting; unit: Unit }): void {
        this.splEntries.push(new SPLEntry(this, new UnitValue(0, selectedWeighting.unit), selectedWeighting.weighting));
    }

    public removeEntry(entry: SPLEntry): void {
        // Find index
        const index = this.splEntries.findIndex((x) => x === entry);

        if (index === undefined) return;

        this.splEntries.splice(index, 1);
    }

    private static stringToSelectedUnit(str: string): selectableWeighting {
        const unit = SPLMonitorModule.availableWeightings.find((x) => x.unit.unit == str);
        return unit ? unit : SPLMonitorModule.availableWeightings[0];
    }

    public openStreamerSPLSettingsModal(): void {
        if (this.streamerSPLSettingsModal && this.streamerSPLSettingsModal.isOpen) {
            this.streamerSPLSettingsModal.cancel();
            this.streamerSPLSettingsModal = undefined;
            return;
        }
        this.moduleInstance
            .createModal<SettingsModalState<SPLMonitorModule>, void>(WorkflowModalCatalog.StreamerSPLSettingsModal)
            .withState({ belongsTo: this })
            .whenClosed(() => {
                this.streamerSPLSettingsModal = undefined;
            })
            .show()
            .then((x) => {
                this.streamerSPLSettingsModal = x;
            });
    }
    //#endregion
}
